import { XMarkIcon, StopIcon, ArrowUpTrayIcon } from "@heroicons/react/24/solid";
import cn from "classnames";
import { useEffect, useRef, useState } from "react";

interface UploadProgressToastProps {
  progress: number;
  fileName?: string;
  onCancel: () => void;
  cancelled?: boolean;
  remainingSeconds?: number;
  fileType?: "narrative" | "interaction";
  currentFileIndex?: number;
  totalFiles?: number;
  indeterminate?: boolean;
  statusText?: string;
  title?: string;
}

const formatRemainingTime = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds} second${seconds !== 1 ? "s" : ""} remaining`;
  } else if (seconds < 3600) {
    const minutes = Math.ceil(seconds / 60);
    return `${minutes} minute${minutes !== 1 ? "s" : ""} remaining`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.ceil((seconds % 3600) / 60);
    if (minutes === 0) {
      return `${hours} hour${hours !== 1 ? "s" : ""} remaining`;
    }
    return `${hours} hour${hours !== 1 ? "s" : ""} ${minutes} min remaining`;
  }
};

const UploadProgressToast = ({
  progress,
  fileName,
  onCancel,
  cancelled = false,
  remainingSeconds,
  fileType,
  currentFileIndex,
  totalFiles,
  indeterminate = false,
  statusText,
  title: titleOverride,
}: UploadProgressToastProps) => {
  const fileNameRef = useRef<HTMLParagraphElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollDistance, setScrollDistance] = useState(0);

  useEffect(() => {
    if (fileNameRef.current && containerRef.current && fileName) {
      // Calculate how much we need to scroll
      const textWidth = fileNameRef.current.scrollWidth;
      const containerWidth = containerRef.current.clientWidth;
      const overflow = textWidth - containerWidth;

      if (overflow > 0) {
        setScrollDistance(overflow);
      } else {
        setScrollDistance(0);
      }
    }
  }, [fileName]);

  return (
    <div
      data-tour="upload-toast"
      className={cn(
        "max-w-md w-full bg-base-100 shadow-lg rounded-lg pointer-events-auto flex flex-col transition-all duration-200",
        "animate-[enter_0.2s_ease-out]",
        cancelled
          ? "ring-2 ring-red-500"
          : "ring-1 ring-black ring-opacity-5 dark:ring-base-300 dark:ring-opacity-100"
      )}
    >
      <div className="p-4">
        <div className={cn(
          "flex items-start justify-between",
          !cancelled && "mb-3"
        )}>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 relative w-5 h-5">
              <ArrowUpTrayIcon
                className={cn(
                  "h-5 w-5 text-blue-500 absolute inset-0 transition-opacity duration-300",
                  cancelled ? "opacity-0" : "opacity-100"
                )}
              />
              <StopIcon
                className={cn(
                  "h-5 w-5 text-red-500 absolute inset-0 transition-opacity duration-300",
                  cancelled ? "opacity-100" : "opacity-0"
                )}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="relative h-5 flex items-center gap-2">
                <p
                  className={cn(
                    "text-sm font-medium transition-opacity duration-300 text-base-content whitespace-nowrap",
                    cancelled ? "opacity-0" : "opacity-100"
                  )}
                >
                  {titleOverride
                    ? titleOverride
                    : totalFiles && totalFiles > 1
                      ? `Uploading ${currentFileIndex}/${totalFiles}`
                      : "Uploading audio"}
                </p>
                {fileType && !cancelled && (
                  <span
                    className={cn(
                      "text-xs px-1.5 py-0.5 rounded font-medium",
                      fileType === "narrative"
                        ? "bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300"
                        : "bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400"
                    )}
                  >
                    {fileType === "narrative" ? "Narrative" : "Interaction"}
                  </span>
                )}
                <p
                  className={cn(
                    "text-sm font-medium absolute inset-0 transition-opacity duration-300 text-red-700 dark:text-red-400 whitespace-nowrap",
                    cancelled ? "opacity-100" : "opacity-0"
                  )}
                >
                  Upload cancelled
                </p>
              </div>
              {fileName && !cancelled && (
                <div ref={containerRef} className="overflow-hidden mt-0.5">
                  <p
                    ref={fileNameRef}
                    className="text-xs text-base-content/60 whitespace-nowrap"
                    style={
                      scrollDistance > 0
                        ? {
                            animation: "scroll-text 8s ease-in-out infinite",
                            animationPlayState: "running",
                          }
                        : undefined
                    }
                  >
                    {fileName}
                  </p>
                </div>
              )}
            </div>
          </div>
          {!cancelled && (
            <button
              onClick={onCancel}
              className="flex-shrink-0 ml-3 rounded-md hover:bg-gray-100 dark:hover:bg-base-200 p-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Cancel upload"
              data-tour="cancel-upload"
            >
              <XMarkIcon className="h-5 w-5 text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70" />
            </button>
          )}
        </div>

        {!cancelled && (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="w-full bg-base-300 rounded-full h-2.5 overflow-hidden">
                  {indeterminate ? (
                    <div className="h-2.5 rounded-full animate-indeterminate-progress bg-blue-500 relative overflow-hidden w-1/3">
                      <div className="absolute inset-0 animate-progress-shimmer bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                    </div>
                  ) : (
                    <div
                      className="bg-blue-500 h-2.5 rounded-full transition-all duration-300 ease-out relative overflow-hidden"
                      style={{ width: `${progress}%` }}
                    >
                      <div className="absolute inset-0 animate-progress-shimmer bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                    </div>
                  )}
                </div>
              </div>
              {!indeterminate && (
                <span className="text-sm font-medium text-gray-700 dark:text-base-content/80 tabular-nums min-w-[3ch]">
                  {progress}%
                </span>
              )}
            </div>
            <div className="relative h-4 flex items-center justify-center">
              <p
                className={cn(
                  "text-xs text-center absolute inset-0 transition-opacity duration-300",
                  statusText
                    ? "opacity-100 text-base-content/60"
                    : remainingSeconds !== undefined
                      ? "opacity-100 text-base-content/60"
                      : "opacity-0"
                )}
              >
                {statusText || (remainingSeconds !== undefined && formatRemainingTime(remainingSeconds))}
              </p>
              <p
                className={cn(
                  "text-xs text-center italic absolute inset-0 transition-opacity duration-300",
                  !statusText && remainingSeconds === undefined && progress < 100 && !indeterminate
                    ? "opacity-100 text-base-content/40"
                    : "opacity-0"
                )}
              >
                Calculating time remaining...
              </p>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes scroll-text {
          0%, 15% {
            transform: translateX(0);
          }
          50%, 65% {
            transform: translateX(-${scrollDistance}px);
          }
          100% {
            transform: translateX(0);
          }
        }

        @keyframes progress-shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(200%);
          }
        }

        .animate-progress-shimmer {
          animation: progress-shimmer 2s ease-in-out infinite;
        }

        @keyframes indeterminate-progress {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(300%);
          }
        }

        .animate-indeterminate-progress {
          animation: indeterminate-progress 1.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default UploadProgressToast;
